/**
 * The route guard (release 3.1, persona D6; SPEC.md 8.4). Every page of the
 * persona specification renders only when the generated table allows it for
 * the caller (`decidePath`); otherwise the refusal page says why. The guard
 * used to be "has a token" (AppShell), so any role could open any page by URL
 * and meet a wall of 403s.
 *
 * Fail closed: nothing renders until the caller's access is known, and a
 * caller whose access cannot be read sees neither the page nor a guess.
 * A path that is not a page of the specification (a legacy redirect, the
 * router's not-found) is left to the router.
 *
 * Hooks for the persona walk (SPEC.md 9.1): the allowed page is wrapped in an
 * element carrying `data-page="<spec id>"`; a refused one renders
 * `data-testid="role-refusal"`.
 */
import type { ReactNode } from 'react';

import { decidePath, useCaller } from '@/lib/access';
import { AccessUnavailable, RoleRefusal } from './RoleRefusal';

export function RouteGuard({ pathname, children }: { pathname: string; children: ReactNode }) {
  const access = useCaller();
  if (access.status === 'error') return <AccessUnavailable onRetry={access.retry} />;
  if (access.status === 'loading') {
    return (
      <div data-testid="access-loading" className="p-8 text-sm text-muted-foreground" aria-busy="true">
        Checking your access...
      </div>
    );
  }
  const decided = decidePath(pathname, access.caller, access.me);
  if (!decided) return <>{children}</>;
  if (!decided.decision.allowed) {
    return <RoleRefusal page={decided.page} decision={decided.decision} role={access.me.role} onRetry={access.retry} />;
  }
  return (
    <div data-page={decided.page.id} className="contents">
      {children}
    </div>
  );
}
